import { type FloorSize } from './room-options';
import { parseSeed } from './seed';

/**
 * What the URL asks the client to do.
 *
 * One parser for the whole query string, because the answers travel together
 * and a caller that read them separately could ask for a combination that does
 * not exist — joining a room *and* choosing its seed, say, when the floor of a
 * room that already exists was decided before this client arrived.
 *
 * Everything here is user input, so nothing throws: a malformed value falls
 * back, exactly as `./seed` does with a malformed seed. The URL is the only
 * launcher this game has until there is a lobby.
 *
 * And everything here is user input in the *other* sense too: what this parses
 * is what the client will **ask** a room for, never what it will get. The seed
 * is granted because choosing your own cave is nothing to gain; the population
 * and the floor size are refused unless the server was started with the key
 * offered beside them, which no production server is. All of it is validated
 * again on arrival by `apps/game`, which is the only side whose answer counts.
 */

/** Where the Colyseus room lives when the build was not told otherwise. */
export const DEFAULT_GAME_URL = 'ws://localhost:2567';

const ROOM_PARAMETER = 'room';
const SOLO_PARAMETER = 'solo';
const POPULATION_PARAMETER = 'population';
const WIDTH_PARAMETER = 'width';
const HEIGHT_PARAMETER = 'height';
const FIXTURE_KEY_PARAMETER = 'fixtureKey';

export interface Launch {
  /**
   * The floor to ask for when *creating* a room, and the cave to draw when
   * there is no room at all. Ignored when joining an existing room by id: that
   * room's floor was grown before this client arrived, and its `LevelState` is
   * the only thing that decides what is drawn.
   */
  readonly seed: number;
  /** An existing room to join by id, or `null` to create one. */
  readonly roomId: string | null;
  /**
   * The renderer sandbox: draw the cave, run the local player's prediction, and
   * connect to nothing.
   *
   * It exists because the browser suite has to put the renderer in arrangements
   * a live room cannot produce on demand — an entity standing between two
   * particular rocks, five capsules on one diagonal — and because a cave is
   * worth looking at without a game server running. It is not a play mode:
   * nobody else is there, nothing is authoritative, and every rule it runs is
   * the same code the connected client runs (see `./world-view`).
   */
  readonly solo: boolean;
  /**
   * How many monsters to **ask** a created room for, or `null` to leave it to
   * the server. Ignored when joining an existing room, like the seed.
   *
   * A request, and one that is normally refused. The count of monsters on a
   * floor is its difficulty and `docs/GAME-DESIGN.md` gives it to the server
   * outright, so `apps/game` grants it only to a caller holding the key that
   * server process was started with — and no production server is started with
   * one. It is parsed here because the browser suite needs a floor with nothing
   * standing on it (`TODO.md` #310, #311) and because a URL a client can type is
   * exactly the sort of thing the server has to be able to refuse.
   *
   * Nothing here checks the *range*: that is the server's rule, and a range
   * checked on both sides is a range the two sides can disagree about.
   */
  readonly population: number | null;
  /**
   * How big a floor to **ask** a created room for, or `null` to leave the size
   * to the server. Ignored when joining an existing room, like the seed.
   *
   * The population's twin, and a larger lever than it. The stairs are placed at
   * the greatest walking distance from the entrance, so the size of a floor is
   * the *length* of its run and the run ends on a staircase down to better loot
   * — a client that could shrink its own floor would descend as fast as it could
   * click. So `apps/game` grants it only to a caller holding the key that server
   * process was started with, and no production server is started with one. It
   * is parsed here because the browser suite needs a floor whose exit is a
   * seventeen-second walk rather than a thirty-seven-second one (`TODO.md`
   * #479), and because a URL a client can type is exactly the sort of thing the
   * server has to be able to refuse.
   *
   * **Both dimensions or neither**, which is the one shape this parser insists
   * on. The room reads "nobody asked" off both being absent and answers a lone
   * dimension `malformed` — then grows the floor it would have grown anyway,
   * silently. So a URL that states half a size states no size: `FloorSize`
   * cannot hold half of one, and the request that would be quietly ignored is
   * never made. Nothing here checks the *range*, which is the server's rule and
   * would be a second copy of `MIN_FLOOR_SIZE` if it were here.
   */
  readonly size: FloorSize | null;
  /** The key offered with `population` and with the size, or `null` when none was. */
  readonly fixtureKey: string | null;
}

/**
 * A whole number the URL actually stated, or `null`.
 *
 * Shared by the population and by both dimensions, because all three are counts
 * of things — monsters, cells across, cells down — and a URL that says `banana`,
 * `1.5` or nothing at all has stated none of them. `Number('')` is zero, which
 * is why a blank is answered before the conversion rather than after it.
 */
const parseCount = (raw: string | null): number | null => {
  if (raw === null || raw.trim() === '') return null;

  const count = Number(raw);

  return Number.isSafeInteger(count) ? count : null;
};

/**
 * The floor size the URL asked for, or `null` — **both dimensions or neither**.
 *
 * A pair, decided in one place, because that is the only way "half a size is not
 * a size" can be true of everything downstream: a `Launch` that could carry a
 * width alone would put a lone `width` on the create options, and the room's
 * answer to that is to grow its own floor and say nothing (see `Launch.size`).
 * A dimension the URL stated badly is a dimension it did not state, so
 * `?width=48&height=banana` is not a size either.
 */
const parseSize = (rawWidth: string | null, rawHeight: string | null): FloorSize | null => {
  const width = parseCount(rawWidth);
  const height = parseCount(rawHeight);

  return width === null || height === null ? null : { width, height };
};

/**
 * Reads the query string.
 *
 * `?room=` wins over `?seed=` when both are given, and says so by leaving the
 * seed alone rather than by clearing it: the seed is still what a *created*
 * room would have used, and keeping it makes the precedence one comparison at
 * the point of use instead of a value that silently changed meaning.
 */
export const parseLaunch = (search: string): Launch => {
  const parameters = new URLSearchParams(search);
  const room = parameters.get(ROOM_PARAMETER)?.trim() ?? '';
  const fixtureKey = parameters.get(FIXTURE_KEY_PARAMETER)?.trim() ?? '';

  return {
    seed: parseSeed(search),
    roomId: room === '' ? null : room,
    // Present at all, whatever it says — a flag in a URL is either there or it
    // is not, and `?solo=false` meaning "connected" is a trap rather than a
    // feature.
    solo: parameters.has(SOLO_PARAMETER),
    population: parseCount(parameters.get(POPULATION_PARAMETER)),
    size: parseSize(parameters.get(WIDTH_PARAMETER), parameters.get(HEIGHT_PARAMETER)),
    fixtureKey: fixtureKey === '' ? null : fixtureKey,
  };
};

/**
 * The endpoint to connect to, given whatever the build was configured with.
 *
 * A blank value is treated as absent because that is what an unset variable
 * looks like after a shell has expanded it — the same trap `readPort` handles
 * on the server side.
 */
export const gameEndpoint = (configured: string | undefined): string => {
  const trimmed = configured?.trim() ?? '';

  return trimmed === '' ? DEFAULT_GAME_URL : trimmed;
};

/** `ws://` → `http://`, `wss://` → `https://`. Anything else is left as it was. */
const SOCKET_SCHEME = /^ws(s?):\/\//u;

/**
 * The same game server, as something a `fetch` can address.
 *
 * The run directory is two ordinary HTTP routes on the Colyseus process
 * (`@d2/schema`'s `RUNS_PATH`) rather than a matchmaking call, because 0.17
 * exposes no other way to ask what is open. So this client needs the game's
 * **origin** as well as its socket URL — and it derives one from the other
 * rather than reading a second environment variable, because there is one game
 * server and a build told about it twice is a build where exactly one of the
 * two is wrong.
 *
 * The scheme is the whole of the conversion and it is not cosmetic: a page
 * served over https may not fetch http at all — the browser refuses it before
 * the request leaves — so a production build that kept `wss` or dropped to
 * `http` would have a lobby that is permanently, silently empty.
 */
export const gameOrigin = (configured: string | undefined): string => {
  const origin = gameEndpoint(configured).replace(SOCKET_SCHEME, 'http$1://');

  // `RUNS_PATH` starts with a slash, and `…:2567//runs` is a 404 nobody would
  // read as a misconfiguration.
  return origin.endsWith('/') ? origin.slice(0, -1) : origin;
};
